import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box } from '@mui/material';

import Login from './components/Login';
import Dashboard from './components/Dashboard';
import BusinessAnalytics from './components/BusinessAnalytics';
import OwnerApproval from './components/OwnerApproval';
import DriverApproval from './components/DriverApproval';
import DriverList from './components/DriverList';
import OwnerList from './components/OwnerList';
import RateManagement from './components/RateManagement';
import FeedbackView from './components/FeedbackView';
import MembershipManagement from './components/MembershipManagement';
import ZoneManagement from './components/ZoneManagement';
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import VehicleApproval from './components/VehicleApproval';
import PendingMemberships from './components/PendingMemberships';
import VehicleList from './components/VehicleList';
import BookingManagement from './components/BookingManagement';
import CustomerDetails from './components/CustomerDetails';
import RazorpaySettings from './components/RazorpaySettings';
import ZoneDriverPricing from './components/ZoneDriverPricing';
import CustomerCmsSettings from './components/CustomerCmsSettings';
import DriverCmsSettings from './components/DriverCmsSettings';
import LiveMap from './components/LiveMap';
import PushCampaigns from './components/PushCampaigns';
import { CustomModalProvider } from './components/CustomModal';

import './i18n';
import './index.css';

const theme = createTheme({
  palette: {
    primary: {
      main: '#116611',
      light: '#55AA22',
      dark: '#0a400a',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#55AA22',
      light: '#99CC77',
      dark: '#338800',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f0f7f0',
      paper: '#ffffff',
    },
    success: {
      main: '#338800',
    },
    error: {
      main: '#c62828',
    },
    warning: {
      main: '#e65100',
    },
    text: {
      primary: '#0d2b0d',
      secondary: '#3d5c3d',
    },
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    h4: { fontWeight: 800 },
    h5: { fontWeight: 700 },
    h6: { fontWeight: 700 },
    subtitle1: { fontWeight: 600 },
    button: { fontWeight: 600, textTransform: 'none', letterSpacing: 0.4 },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          paddingTop: 10,
          paddingBottom: 10,
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #116611 0%, #338800 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #338800 0%, #55AA22 100%)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { borderRadius: 14 },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            background: 'linear-gradient(90deg, #116611 0%, #338800 100%)',
            color: '#ffffff',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 8 },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
          },
        },
      },
    },
  },
});

const SIDEBAR_WIDTH = 260;
const SIDEBAR_COLLAPSED_WIDTH = 72;

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    const adminToken = localStorage.getItem('adminToken');
    if (adminToken) setIsAuthenticated(true);
    setLoading(false);
  }, []);

  const handleLogin = () => setIsAuthenticated(true);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminData');
    setIsAuthenticated(false);
  };

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#f0f2f8' }}>
          <Box sx={{ width: 40, height: 40, border: '4px solid #1a237e', borderTopColor: '#00bcd4', borderRadius: '50%', animation: 'spin 0.8s linear infinite', '@keyframes spin': { to: { transform: 'rotate(360deg)' } } }} />
        </Box>
      </ThemeProvider>
    );
  }

  const drawerWidth = sidebarOpen ? SIDEBAR_WIDTH : SIDEBAR_COLLAPSED_WIDTH;

  return (
    <CustomModalProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
          {isAuthenticated && (
            <Sidebar
              open={sidebarOpen}
              onToggle={() => setSidebarOpen(!sidebarOpen)}
              width={SIDEBAR_WIDTH}
              collapsedWidth={SIDEBAR_COLLAPSED_WIDTH}
            />
          )}
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              minHeight: '100vh',
              display: 'flex',
              flexDirection: 'column',
              ml: isAuthenticated ? `${drawerWidth}px` : 0,
              transition: 'margin-left 0.28s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            {isAuthenticated && (
              <Navbar
                onLogout={handleLogout}
                sidebarOpen={sidebarOpen}
                onSidebarToggle={() => setSidebarOpen(!sidebarOpen)}
              />
            )}
            <Box sx={{ flexGrow: 1, p: { xs: 2, sm: 3 }, maxWidth: '100%' }}>
              <Routes>
                <Route path="/login" element={isAuthenticated ? <Navigate to="/dashboard" /> : <Login onLogin={handleLogin} />} />
                <Route path="/dashboard" element={isAuthenticated ? <Dashboard /> : <Navigate to="/login" />} />
                <Route path="/analytics" element={isAuthenticated ? <BusinessAnalytics /> : <Navigate to="/login" />} />
                <Route path="/owner-approval" element={isAuthenticated ? <OwnerApproval /> : <Navigate to="/login" />} />
                <Route path="/vehicle-approval" element={isAuthenticated ? <VehicleApproval /> : <Navigate to="/login" />} />
                <Route path="/pending-memberships" element={isAuthenticated ? <PendingMemberships /> : <Navigate to="/login" />} />
                <Route path="/driver-approval" element={isAuthenticated ? <DriverApproval /> : <Navigate to="/login" />} />
                <Route path="/rate-management" element={isAuthenticated ? <RateManagement /> : <Navigate to="/login" />} />
                <Route path="/zone-driver-pricing" element={isAuthenticated ? <ZoneDriverPricing /> : <Navigate to="/login" />} />
                <Route path="/feedback" element={isAuthenticated ? <FeedbackView /> : <Navigate to="/login" />} />
                <Route path="/zone-management" element={isAuthenticated ? <ZoneManagement /> : <Navigate to="/login" />} />
                <Route path="/membership-management" element={isAuthenticated ? <MembershipManagement /> : <Navigate to="/login" />} />
                <Route path="/driver-list" element={isAuthenticated ? <DriverList /> : <Navigate to="/login" />} />
                <Route path="/owner-list" element={isAuthenticated ? <OwnerList /> : <Navigate to="/login" />} />
                <Route path="/vehicle-list" element={isAuthenticated ? <VehicleList /> : <Navigate to="/login" />} />
                <Route path="/live-map" element={isAuthenticated ? <LiveMap /> : <Navigate to="/login" />} />
                <Route path="/push-campaigns" element={isAuthenticated ? <PushCampaigns /> : <Navigate to="/login" />} />
                <Route path="/bookings" element={isAuthenticated ? <BookingManagement /> : <Navigate to="/login" />} />
                <Route path="/customers" element={isAuthenticated ? <CustomerDetails /> : <Navigate to="/login" />} />
                <Route path="/razorpay-settings" element={isAuthenticated ? <RazorpaySettings /> : <Navigate to="/login" />} />
                <Route path="/customer-cms" element={isAuthenticated ? <CustomerCmsSettings /> : <Navigate to="/login" />} />
                <Route path="/driver-cms" element={isAuthenticated ? <DriverCmsSettings /> : <Navigate to="/login" />} />

                <Route path="*" element={<Navigate to={isAuthenticated ? '/dashboard' : '/login'} />} />
              </Routes>
            </Box>
          </Box>
        </Box>
      </ThemeProvider>
    </CustomModalProvider>
  );
}

export default App;
